import type { ComponentProps } from 'react';
import { EstimateLineItemsSection } from '@/components/organisms/estimates/EstimateLineItemsSection';
import { LineItemPicker, type PickedLineItem } from '@/components/organisms/LineItemPicker';
import { nonRepeatableLineItemIds } from '@/lib/sales/line-economics';
import type { MembershipPlan } from '@/components/organisms/pricebook/support/membership-plan-shared';

type LineItemsSectionProps = ComponentProps<typeof EstimateLineItemsSection>;

/**
 * The searches a sales document is built from.
 *
 * ONE definition, used by estimate/proposal editors and both template editors,
 * because a template is a sales document that gets reused — an author who
 * writes one and an operator who writes the live document are doing the same
 * job and must be given the same tools. Built separately, the template editors
 * drifted away from the sectioned searches and line behavior of live documents.
 *
 * Returns the picker props rather than rendering them, so each surface still
 * decides what it does with a picked line.
 */
export function documentLinePickers({
  onAdd,
  onAddConfiguredPart,
  lineItems,
  membershipCustomerId,
  membershipServiceAddressId,
  getMembershipPotentialSavings,
}: {
  onAdd: (item: PickedLineItem) => void;
  /** Add a part to what a SERVICE contains, from inside its configuration. */
  onAddConfiguredPart: (parentIndex: number, item: PickedLineItem) => void;
  /**
   * The document's current lines. The LINES are asked for, not a ready-made id
   * list, because which of them may be offered again is a rule — see
   * `nonRepeatableLineItemIds` — and a caller handing over ids would be a caller
   * deciding it.
   */
  lineItems: readonly { lineItemId?: string | null; type?: string | null }[];
  membershipCustomerId?: string | null;
  membershipServiceAddressId?: string | null;
  getMembershipPotentialSavings?: (plan: MembershipPlan) => number;
}): Pick<LineItemsSectionProps, 'picker' | 'sectionedPicker' | 'componentPicker'> {
  // NOT-638 — a MATERIAL or EQUIPMENT the document already carries stays
  // searchable, because a service that includes parts materializes a real line
  // per part: excluding what was already there hid a service's own fittings from
  // the very searches an operator needs when the work took more of one than the
  // service assumes, leaving a custom line — which drops `lineItemId` and the
  // cost tracking with it — as the only way through. Every other type keeps its
  // exclusion; `canRepeatOnDocument` carries the reasoning per type.
  //
  // Safe here because these pickers APPEND what is picked. The proposal
  // OptionCard still excludes everything it holds, and should: its add merges a
  // re-added item into the existing row and keys rows by line identity, so a
  // second row of one catalog item would collide with the first.
  const excludeIds = nonRepeatableLineItemIds(lineItems);

  return {
    picker: (
      <LineItemPicker
        onAdd={onAdd}
        excludeIds={excludeIds}
        // NOT-590 — picking a service that includes parts adds the service line
        // plus a real priced line per part.
        expandComponents
        // A material or equipment item quoted on its own starts unbilled. Safe
        // because every surface using these pickers renders the Chargeable
        // tick, so the operator sells it separately in one click.
        partsStartUnbilled
        membershipCustomerId={membershipCustomerId}
        membershipServiceAddressId={membershipServiceAddressId}
        getMembershipPotentialSavings={getMembershipPotentialSavings}
      />
    ),
    // Each section searches only its own kind, so you never have to tell a
    // "Drain line" service from a "Drain line" material in one mixed result list.
    sectionedPicker: (section) => (
      <LineItemPicker
        onAdd={onAdd}
        excludeIds={excludeIds}
        expandComponents
        allowedTypes={section.allowedTypes}
        placeholder={section.placeholder}
        partsStartUnbilled
        membershipCustomerId={membershipCustomerId}
        membershipServiceAddressId={membershipServiceAddressId}
        getMembershipPotentialSavings={getMembershipPotentialSavings}
      />
    ),
    // A part the SERVICE did not come with. It joins what the service contains —
    // so the service reprices from it — and lands as an ordinary line whose
    // money is inside the service, exactly like a part the catalog put there.
    componentPicker: (_parent, parentIndex) => (
      <LineItemPicker
        onAdd={(picked) => onAddConfiguredPart(parentIndex, picked)}
        allowedTypes={['MATERIAL', 'EQUIPMENT']}
        placeholder="Add a material or equipment…"
      />
    ),
  };
}
